import React, { useState, useEffect } from "react";
import { Modal, Form, Row, Col, Button } from "react-bootstrap";
import { Tooltip, IconButton } from "@mui/material";
import { Visibility, Delete, CheckCircle, Cancel, Javascript } from "@mui/icons-material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import axios from "axios";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import Loader from "../Loader";
import Select from 'react-select';
import {
  GENDER_TYPES,
  STATUS_TYPES,
  MARTIAL_STATUS_TYPES,
  MEANS_OF_TRANSPORT_TYPES,
  HEALTH_PROFESSIONS,
  DISABILITY,
  ATTACHMENT_TYPES,
  PROVIDER_POSITIONS,
  CAN_SPEAK,
} from "../enum";
import CustomProgressBar from "./customProgressBar";

// Validation schemas for each step
const stepSchemas = [
  Yup.object({
    firstName: Yup.string().required("First name is required"),
    lastName: Yup.string().required("Last name is required"),
    gender: Yup.string().required("Gender is required"),

    birthDate: Yup.date()
      .required("Birth date is required")
      .max(new Date(), "Birth date cannot be in the future"),
    maritalStatus: Yup.string().required("Marital status is required"),
  }),
  Yup.object({
    phoneNumber: Yup.string()
      .matches(/^\+251\d{9}$|^0\d{9}$|^01\d{8}$/, "Invalid phone number")
      .required("Phone number is required"),
    email: Yup.string().email("Invalid email").required("Email is required"),
    address: Yup.string().required("Address is required"),
  }),
  Yup.object({
    driverScince: Yup.date()
      .required("Driver since date is required")
      .max(new Date(), "Date cannot be in the future"),
    profession: Yup.number().required("Profession is required"),
    positions: Yup.string().required("Position is required"),
    ambulanceId: Yup.string().when("positions", {
      is: PROVIDER_POSITIONS.AMBULANCE_PROVIDER,
      then: Yup.string().required("Ambulance company is required when position is Ambulance Provider"),
      otherwise: Yup.string().notRequired(),
    }),
    meansOfTransport: Yup.number().required("Means of transport is required"),
    emergencyContactName: Yup.string().required("Emergency contact name is required"),
    emergencyContactPhone: Yup.string()
      .matches(/^\+251\d{9}$|^0\d{9}$|^01\d{8}$/, "Invalid phone number")
      .required("Emergency contact number is required"),
  }),
  Yup.object({
    documents: Yup.array()
      .of(
        Yup.object({
          attachmentType: Yup.string().required("Attachment type is required"),
          file: Yup.mixed().when("editMode", {
            is: false, // When in add mode
            then: Yup.mixed().required("Document file is required"),
            otherwise: Yup.mixed().notRequired(),
          })
            .test("fileType", "Unsupported file type", (value) => {
              if (!value) return true;
              const supportedTypes = ["image/jpg", "image/jpeg", "image/png", "application/pdf"];
              return supportedTypes.includes(value.type);
            })
            .test("fileSize", "File size is too large", (value) => {
              if (!value) return true;
              return value.size <= 5 * 1024 * 1024; // Max 5MB
            }),
        })
      )
      .min(1, "At least one document is required"),
  }),
  Yup.object({
    password: Yup.string().when("editMode", {
      is: false,
      then: Yup.string().required("Password is required"),
      otherwise: Yup.string().notRequired(),
    }),
    confPassword: Yup.string().when("editMode", {
      is: false,
      then: Yup.string()
        .oneOf([Yup.ref("password")], "Passwords must match")
        .required("Confirm password is required"),
      otherwise: Yup.string().notRequired(),
    }),
    status: Yup.string().required("Status is required"),
  }),
];


const DriverMultiStepForm = ({ showModal, hideModal, mode, initialData, refreshDrivers }) => {
  const isEditMode = mode === "edit";

  console.log("initialData", initialData);


  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);

  const [ambulanceCompanies, setAmbulanceCompanies] = useState([]);


  const fetchAmbulanceCompanies = async () => {
    try {
      const response = await axios.get("/ambulance-companies");
      setAmbulanceCompanies(response.data);
    } catch (error) {
      console.error("Error fetching ambulance companies:", error);
    }
  };

  useEffect(() => {
    fetchAmbulanceCompanies();
  }, []);


  const transformAttachmentsToDocuments = (attachments) => {
    return attachments.map((attachment) => ({
      attachmentType: attachment.attachmentType,
      file: null,
      url: attachment.filePath,
      isNew: false,
    }));
  };

  const transformAmbulanceCompany = (ambulanceCompany) => {
    if (!ambulanceCompany) {
      return null;
    }

    return {
      company_id: ambulanceCompany.company_id,
      company_name: ambulanceCompany.company_name,
    };
  };
  const formik = useFormik({
    initialValues: {
      firstName: initialData?.firstName || "",
      middleName: initialData?.middleName || "",
      lastName: initialData?.lastName || "",
      gender: initialData?.gender || "",
      birthDate: initialData?.birthDate || "",
      maritalStatus: initialData?.maritalStatus || "",
      phoneNumber: initialData?.phoneNumber || "",
      email: initialData?.email || "",
      address: initialData?.address || "",
      subCity: initialData?.subCity || "",
      woreda: initialData?.woreda || "",
      canSpeak: (() => {
        const cs = initialData?.canSpeak;
        if (!cs) return [];
        if (Array.isArray(cs)) return cs;
        if (typeof cs === 'object') return Object.values(cs);
        return cs.toString().split(',').map((v) => Number(v.trim()));
      })(),
      houseNumber: initialData?.houseNumber || "",
      driverScince: initialData?.driverScince || "",
      disability: initialData?.disability || "",
      meansOfTransport: initialData?.meansOfTransport || MEANS_OF_TRANSPORT_TYPES.CAR,
      profession: initialData?.profession || "",
      ambulanceId: initialData?.driverAmbulanceCompany ? transformAmbulanceCompany(initialData.driverAmbulanceCompany).company_id : "",
      emergencyContactName: initialData?.emergencyContactName || "",
      emergencyContactPhone: initialData?.emergencyContactPhone || "",
      positions: initialData?.positions || "",
      documents: initialData?.attachments
        ? transformAttachmentsToDocuments(initialData.attachments)
        : [{ attachmentType: "", file: null, url: "", isNew: true }],
      password: "",
      confPassword: "",
      status: initialData?.status || STATUS_TYPES.INACTIVE,
      editMode: isEditMode,
    },
    validationSchema: stepSchemas[step],
    onSubmit: async (values) => {
      if (step < stepSchemas.length - 1) {
        setStep((prev) => prev + 1);
        return;
      }

      setLoading(true);
      try {
        const formData = new FormData();
        Object.keys(values).forEach((key) => {
          if (key === "documents") {
            values.documents.forEach((doc, index) => {
              formData.append(`documents[${index}].attachmentType`, doc.attachmentType);
              formData.append(`documents[${index}].file`, doc.file);
            });
          } else {
            formData.append(key, values[key]);
          }
        });

        if (isEditMode) {
          await axios.put(`/driver/${initialData.id}`, formData);
          toast.success("Driver updated successfully!");
        } else {
          await axios.post("/drivers", formData);
          toast.success("Driver added successfully!");
        }

        refreshDrivers();
        hideModal();
      } catch (error) {
        console.error(error);

        // Handle specific backend errors
        if (error.response && error.response.data) {
          const errorMessage = error.response.data.msg || error.response.data.message || error.response.data.error;

          // Check for phone number duplicate error
          if (errorMessage && errorMessage.toLowerCase().includes('phone number already registered')) {
            // Navigate back to step 2 (contact info step) where phone number is entered
            setStep(1);

            // Set phone number field error
            formik.setFieldError('phoneNumber', errorMessage);

            toast.error(errorMessage);
          } else if (errorMessage) {
            toast.error(errorMessage);
          } else {
            toast.error("An error occurred while submitting the form.");
          }
        } else {
          toast.error("An error occurred while submitting the form.");
        }
      } finally {
        setLoading(false);
      }
    },
  });

  const handleAddDocument = () => {
    formik.setFieldValue("documents", [
      ...formik.values.documents,
      { attachmentType: "", file: null, url: "", isNew: true },
    ]);
  };

  const fetchFileWithAuth = async (fileURL) => {
    const token = localStorage.getItem('d4uToken');

    const headers = {
      Authorization: `Bearer ${token}`,
    };

    try {
      const response = await axios.get(fileURL, {
        headers: headers,
        responseType: 'blob',
      });

      const blob = new Blob([response.data], { type: response.headers['content-type'] });
      const fileBlobURL = URL.createObjectURL(blob);

      return fileBlobURL;
    } catch (error) {
      console.error("Error fetching file with Bearer token:", error);
      throw new Error("Failed to fetch the file.");
    }
  };

  const handlePreview = async (index) => {
    const document = formik.values.documents[index];

    if (!document.file && !document.url) {
      console.error('No file or URL to preview');
      return;
    }

    let fileURL = '';

    if (document.file) {
      if (document.file instanceof Blob) {
        fileURL = URL.createObjectURL(document.file);
      } else {
        console.error('Invalid file format for local file');
        return;
      }
    } else if (document.url) {
      const backendHost = process.env.REACT_APP_BASE_URL;
      if (document.url.startsWith('/')) {
        fileURL = backendHost + document.url;
      } else if (document.url.startsWith('http://') || document.url.startsWith('https://')) {
        fileURL = document.url;
      } else {
        console.error('Invalid URL format');
        return;
      }
    }

    if (fileURL) {
      if (fileURL.startsWith("http://") || fileURL.startsWith("https://")) {
        const fileBlobURL = await fetchFileWithAuth(fileURL);
        window.open(fileBlobURL, "_blank");
      } else {
        window.open(fileURL, "_blank");
      }
    }
  };


  const handleRemoveDocument = (index) => {
    const updatedDocuments = [...formik.values.documents];
    updatedDocuments.splice(index, 1);
    formik.setFieldValue("documents", updatedDocuments);
  };

  const renderDocumentFields = () => {
    const attachmentTypeOptions = Object.entries(ATTACHMENT_TYPES).map(
      ([key, value]) => ({ label: key.replace(/_/g, " "), value })
    );

    return (
      <>
        {formik.values.documents.map((doc, index) => (
          <Row key={index} className="align-items-center mb-3">
            <Col md={5}>
              <Form.Group>
                <Form.Label>Attachment Type</Form.Label>
                <Form.Control
                  as="select"
                  name={`documents[${index}].attachmentType`}
                  onChange={formik.handleChange}
                  value={formik.values.documents[index]?.attachmentType || ""}
                  isInvalid={
                    formik.touched.documents?.[index]?.attachmentType &&
                    formik.errors.documents?.[index]?.attachmentType
                  }
                  style={{
                    pointerEvents: doc.isNew ? "auto" : "none",
                    backgroundColor: doc.isNew ? "white" : "#f8f9fa",
                    opacity: doc.isNew ? 1 : 0.7,
                  }}
                >
                  <option value="">Select Attachment Type</option>
                  {attachmentTypeOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Control>
                <Form.Control.Feedback type="invalid">
                  {formik.errors.documents?.[index]?.attachmentType}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group>
                <Form.Label>Upload Document</Form.Label>
                <Form.Control
                  type="file"
                  name={`documents[${index}].file`}
                  onChange={(e) => {
                    const file = e.currentTarget.files[0];
                    formik.setFieldValue(`documents[${index}].file`, file);
                  }}
                  isInvalid={
                    formik.touched.documents?.[index]?.file &&
                    formik.errors.documents?.[index]?.file
                  }
                />
                <Form.Control.Feedback type="invalid">
                  {formik.errors.documents?.[index]?.file}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={3} className="d-flex align-items-center">
              <Tooltip title="Preview Document">
                <IconButton onClick={() => handlePreview(index)}>
                  <Visibility style={{ fontSize: "1.5rem", cursor: "pointer" }} />
                </IconButton>
              </Tooltip>
              {doc.file || doc.url ? (
                <Tooltip title="Document Uploaded">
                  <CheckCircle style={{ fontSize: "1.5rem", color: "green" }} />
                </Tooltip>
              ) : (
                <Tooltip title="No Document Uploaded">
                  <Cancel style={{ fontSize: "1.5rem", color: "red" }} />
                </Tooltip>
              )}
              <Tooltip title="Remove Document">
                <IconButton onClick={() => handleRemoveDocument(index)}>
                  <Delete style={{ fontSize: "1.5rem", color: "red" }} />
                </IconButton>
              </Tooltip>
            </Col>
          </Row>
        ))}
      </>
    );
  };



  const renderField = (
    name,
    type,
    placeholder,
    options = null,
    isMulti = false
  ) => {
    const feedback = (
      <Form.Control.Feedback type="invalid">
        {formik.errors[name]}
      </Form.Control.Feedback>
    );

    return (
      <Form.Group className="mb-3">
        <Form.Label>{placeholder}</Form.Label>
        {options ? (
          isMulti ? (
            // React Select for multi-select
            <>
              <Select
                isMulti={true}
                name={name}
                placeholder={`Select ${placeholder}`}
                options={Object.entries(options).map(([key, value]) => ({
                  value,
                  label: key.replace(/_/g, " "),
                }))}
                value={Object.entries(options)
                  .map(([key, val]) => ({
                    value: val,
                    label: key.replace(/_/g, " "),
                  }))
                  .filter(option =>
                    Array.isArray(formik.values[name])
                      ? formik.values[name].includes(option.value)
                      : formik.values[name] === option.value
                  )}
                onChange={selected => {
                  const values = selected
                    ? selected.map(option => option.value)
                    : [];
                  formik.setFieldValue(name, values);
                }}
                className={
                  formik.touched[name] && formik.errors[name] ? "is-invalid" : ""
                }
              />
              {feedback}
            </>
          ) : (
            // Native select for single choice
            <>
              <Form.Control
                as="select"
                name={name}
                onChange={formik.handleChange}
                value={formik.values[name]}
                isInvalid={formik.touched[name] && formik.errors[name]}
              >
                <option value="">Select {placeholder}</option>
                {Object.entries(options).map(([key, value]) => (
                  <option key={key} value={value}>
                    {key.replace(/_/g, " ")}
                  </option>
                ))}
              </Form.Control>
              {feedback}
            </>
          )
        ) : (
          // Regular input field
          <>
            <Form.Control
              type={type}
              name={name}
              onChange={formik.handleChange}
              value={formik.values[name]}
              isInvalid={formik.touched[name] && formik.errors[name]}
            />
            {feedback}
          </>
        )}
      </Form.Group>
    );
  };

  const renderStep = () => {
    switch (step) {
      case 0:
        return (
          <>
            {renderField("firstName", "text", "First Name")}
            {renderField("middleName", "text", "Middle Name")}
            {renderField("lastName", "text", "Last Name")}
            {renderField("gender", "text", "Gender", GENDER_TYPES)}
            {renderField("birthDate", "date", "Birth Date")}
            {renderField("maritalStatus", "number", "Marital Status", MARTIAL_STATUS_TYPES)}
          </>
        );
      case 1:
        return (
          <>
            <Form.Group className="mb-3">
              <Form.Label>Phone Number</Form.Label>
              <PhoneInput
                country="et"
                value={formik.values.phoneNumber}
                onChange={(value) => formik.setFieldValue('phoneNumber', '+' + value)}
                enableAreaCodes={true}
                containerClass="phone-input-container"
                inputClass={formik.touched.phoneNumber && formik.errors.phoneNumber ? "form-control is-invalid" : "form-control"}
                inputStyle={{
                  width: '100%',
                  height: '38px',
                  fontSize: '14px'
                }}
              />
              {formik.touched.phoneNumber && formik.errors.phoneNumber && (
                <div className="invalid-feedback" style={{ display: 'block' }}>
                  {formik.errors.phoneNumber}
                </div>
              )}
            </Form.Group>
            {renderField("email", "email", "Email")}
            {renderField("address", "text", "Address")}
            {renderField("subCity", "text", "Sub-City")}
            {renderField("woreda", "text", "Woreda")}
            {renderField("houseNumber", "text", "House Number")}
            {renderField("canSpeak", "text", "Can Speak", CAN_SPEAK, true)}

          </>
        );
      case 2:
        return (
          <>
            {renderField("driverScince", "date", "Driver Since")}
            {renderField("disability", "number", "Disability", DISABILITY)}
            {renderField(
              "meansOfTransport",
              "number",
              "Means of Transport",
              (typeof window !== 'undefined' && localStorage.getItem('userType') === 'ambulance_company')
                ? { AMBULANCE: MEANS_OF_TRANSPORT_TYPES.AMBULANCE }
                : MEANS_OF_TRANSPORT_TYPES
            )}
            {renderField(
              "profession",
              "number",
              "Profession",
              (typeof window !== 'undefined' && localStorage.getItem('userType') === 'ambulance_company')
                ? { DRIVER: HEALTH_PROFESSIONS.DRIVER, EMT: HEALTH_PROFESSIONS.EMT, NURSE: HEALTH_PROFESSIONS.NURSE }
                : HEALTH_PROFESSIONS
            )}
            {renderField(
              "positions",
              "select",
              "Position",
              (typeof window !== 'undefined' && localStorage.getItem('userType') === 'ambulance_company')
                ? { AMBULANCE_PROVIDER: PROVIDER_POSITIONS.AMBULANCE_PROVIDER }
                : PROVIDER_POSITIONS
            )}
            {parseInt(formik.values.positions) === PROVIDER_POSITIONS.AMBULANCE_PROVIDER &&
              (
                <Form.Group controlId="ambulanceId">
                  <Form.Label>Ambulance Company</Form.Label>
                  <Form.Control
                    as="select"
                    value={formik.values.ambulanceId}
                    onChange={formik.handleChange}
                    isInvalid={formik.touched.ambulanceId && formik.errors.ambulanceId}
                  >
                    <option value="">Select Ambulance Company</option>
                    {ambulanceCompanies.map((company) => (
                      <option key={company.company_id} value={company.company_id}>
                        {company.company_name}
                      </option>
                    ))}
                  </Form.Control>
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.ambulanceId}
                  </Form.Control.Feedback>
                </Form.Group>
              )
            }

            {/* {renderField("ambulanceId", "text", "Ambulance ID")} */}
            {renderField("emergencyContactName", "text", "Emergency Contact Name")}
            {renderField("emergencyContactPhone", "text", "Emergency Contact Number")}
          </>
        );
      case 3:
        return (
          <>
            {renderDocumentFields()}
            <Button onClick={handleAddDocument} variant="success" className="mt-3">
              Add New Document
            </Button>
          </>
        );
      case 4:
        return (
          <>
            {!isEditMode && (
              <>
                {renderField("password", "password", "Password")}
                {renderField("confPassword", "password", "Confirm Password")}
              </>
            )}
            {renderField("status", "text", "Status", STATUS_TYPES)}
          </>
        );
      default:
        return null;
    }
  };

  return (
    <Modal style={{ marginTop: "60px" }} show={showModal} onHide={hideModal} size="lg" backdrop={false}>
      <Modal.Header closeButton>
        <Modal.Title>{isEditMode ? "Edit Driver" : "Add Driver"}</Modal.Title>
      </Modal.Header>
      <Modal.Body>{loading ? <Loader /> : renderStep()}</Modal.Body>
      <Modal.Footer className="d-flex justify-content-between">
        {step > 0 && (
          <Button variant="secondary" onClick={() => setStep(step - 1)}>
            Previous
          </Button>
        )}
        <Button variant="primary" onClick={formik.handleSubmit}>
          {step === stepSchemas.length - 1 ? "Submit" : "Next"}
        </Button>
        <CustomProgressBar progress={((step + 1) / stepSchemas.length) * 100} />
      </Modal.Footer>
    </Modal>
  );
};

export default DriverMultiStepForm;
